import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { runTask } from "@/lib/tasks";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/lib/auth";

const SECTIONS = [
  {
    key: "business", title: "The Business",
    intro: "The basics. What you sell and how the machine works.",
    fields: [
      { k: "business_name", label: "Business name", type: "input" },
      { k: "business_model", label: "Business model", hint: "e.g. Agency, SaaS, coach, e-com, course, consultancy" },
      { k: "industry", label: "Industry" },
      { k: "revenue_model", label: "Revenue model", hint: "Subscription, one-time, retainer, tiered, etc." },
      { k: "products_services", label: "Products & services", type: "textarea" },
      { k: "pricing", label: "Pricing", hint: "Rough numbers are fine" },
      { k: "sales_process", label: "Sales process", type: "textarea", hint: "How a stranger becomes a customer" },
      { k: "sales_cycle", label: "Sales cycle length", hint: "e.g. 2 weeks, 3 months" },
      { k: "acquisition_channels", label: "Where customers come from today", type: "textarea" },
    ],
  },
  {
    key: "offer", title: "The Offer",
    intro: "Your Grand Slam Offer, in your own words.",
    fields: [
      { k: "dream_outcome", label: "The dream outcome", type: "textarea", hint: "The transformation the customer buys" },
      { k: "core_promise", label: "Core promise" },
      { k: "deliverables", label: "Deliverables", type: "textarea" },
      { k: "bonuses", label: "Bonuses", type: "textarea" },
      { k: "guarantee", label: "Guarantee" },
      { k: "price", label: "Price" },
      { k: "time_to_result", label: "Time to result" },
      { k: "unique_mechanism", label: "Unique mechanism", type: "textarea", hint: "The non-obvious thing that makes it work" },
      { k: "why_customers_buy", label: "Why customers buy", type: "textarea" },
      { k: "why_customers_dont_buy", label: "Why customers don't buy", type: "textarea" },
    ],
  },
  {
    key: "customer", title: "The Customer",
    intro: "Who they are — and what stops them.",
    fields: [
      { k: "icp", label: "Ideal customer profile (ICP)", type: "textarea" },
      { k: "demographics", label: "Demographics" },
      { k: "psychographics", label: "Psychographics", type: "textarea" },
      { k: "pain_points", label: "Pain points", type: "textarea" },
      { k: "desired_outcomes", label: "Desired outcomes", type: "textarea" },
      { k: "failed_solutions", label: "What they've tried that failed", type: "textarea" },
      { k: "objections", label: "Objections", type: "textarea" },
      { k: "limiting_beliefs", label: "Limiting beliefs", type: "textarea" },
      { k: "purchase_triggers", label: "Purchase triggers", type: "textarea", hint: "Events/moments that make them ready to buy" },
      { k: "faq", label: "FAQs", type: "textarea" },
    ],
  },
  {
    key: "brand", title: "The Brand",
    intro: "Your voice, opinions, and stories.",
    fields: [
      { k: "tone", label: "Tone" },
      { k: "vocabulary", label: "Signature words / phrases", type: "textarea" },
      { k: "humor", label: "Humor — how it shows up (or doesn't)" },
      { k: "opinions", label: "Opinions", type: "textarea" },
      { k: "beliefs", label: "Beliefs", type: "textarea" },
      { k: "contrarian_takes", label: "Contrarian takes", type: "textarea" },
      { k: "stories", label: "Customer stories to reuse", type: "textarea" },
      { k: "founder_story", label: "Founder story", type: "textarea" },
      { k: "topics_yes", label: "Topics you WILL discuss", type: "textarea" },
      { k: "topics_no", label: "Topics you WON'T discuss", type: "textarea" },
    ],
  },
  {
    key: "assets", title: "Existing Assets",
    intro: "Paste anything that helps us learn your voice faster.",
    fields: [
      { k: "website", label: "Website URL" },
      { k: "social_accounts", label: "Social accounts", type: "textarea", hint: "Handles + platforms" },
      { k: "additional_assets", label: "Paste transcripts, testimonials, reviews, previous scripts", type: "textarea", big: true },
    ],
  },
];

const EMPTY = SECTIONS.reduce((acc, s) => { s.fields.forEach(f => acc[f.k] = ""); return acc; }, {});

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const [intake, setIntake] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const nav = useNavigate();
  const { refresh } = useAuth();

  useEffect(() => { document.documentElement.classList.add("dark"); }, []);

  useEffect(() => {
    api.get("/api/brand").then(({ data }) => {
      if (data?.intake) setIntake({ ...EMPTY, ...data.intake });
    }).catch(() => {});
  }, []);

  const set = (k, v) => setIntake((s) => ({ ...s, [k]: v }));

  const saveDraft = async () => {
    setSaving(true);
    try {
      await api.post("/api/brand/intake", intake);
      toast.success("Draft saved");
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const next = async () => {
    await saveDraft();
    if (step < SECTIONS.length - 1) setStep(step + 1);
    else await generate();
  };

  const generate = async () => {
    setGenerating(true);
    toast.message("Synthesizing your Brand Intelligence Profile…", { description: "This can take 30–90 seconds." });
    try {
      await api.post("/api/brand/intake", intake);
      await runTask("/api/brand/generate", null, { intervalMs: 2500, maxMs: 240000 });
      // Auto-approve so the user drops straight into the dashboard
      await api.post("/api/brand/approve").catch(() => {});
      await refresh();
      toast.success("Your Brand Intelligence Profile is ready");
      nav("/app");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Generation failed");
    } finally { setGenerating(false); }
  };

  const section = SECTIONS[step];
  const pct = Math.round(((step + 1) / SECTIONS.length) * 100);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Onboarding · Step {step + 1} of {SECTIONS.length}</div>
              <h1 className="mt-1 font-display font-black text-3xl tracking-tight">{section.title}</h1>
            </div>
            <div className="text-right">
              <div className="font-mono text-xs text-muted-foreground">{pct}%</div>
              <div className="w-40 mt-1"><Progress value={pct} data-testid="onboarding-progress"/></div>
            </div>
          </div>
          <p className="text-muted-foreground">{section.intro}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10 grid gap-5">
        {section.fields.map((f) => (
          <div key={f.k} data-testid={`field-${f.k}`}>
            <Label className="text-xs uppercase tracking-widest">{f.label}</Label>
            {f.hint && <div className="text-xs text-muted-foreground mt-0.5">{f.hint}</div>}
            {f.type === "textarea" ? (
              <Textarea
                data-testid={`input-${f.k}`}
                value={intake[f.k]}
                onChange={(e) => set(f.k, e.target.value)}
                className={`mt-1.5 ${f.big ? "min-h-[220px]" : "min-h-[90px]"}`}
              />
            ) : (
              <Input
                data-testid={`input-${f.k}`}
                value={intake[f.k]}
                onChange={(e) => set(f.k, e.target.value)}
                className="mt-1.5 h-11"
              />
            )}
          </div>
        ))}

        <div className="flex items-center justify-between pt-6 border-t border-border">
          <Button
            variant="outline"
            data-testid="btn-back"
            disabled={step === 0 || generating}
            onClick={() => setStep(step - 1)}
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          <div className="flex items-center gap-3">
            <Button variant="ghost" data-testid="btn-save-draft" onClick={saveDraft} disabled={saving || generating}>
              {saving ? "Saving…" : "Save draft"}
            </Button>
            <Button data-testid="btn-next" onClick={next} disabled={generating} className="min-w-[200px]">
              {generating ? (
                <><Loader2 className="h-4 w-4 animate-spin"/> Synthesizing…</>
              ) : step === SECTIONS.length - 1 ? (
                <><Sparkles className="h-4 w-4"/> Build my Brand Intelligence Profile</>
              ) : (
                <>Next <ArrowRight className="h-4 w-4"/></>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
